import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useStoreInfo, useUpdateStore } from '../hooks/useSettings';
import { BUSINESS_BRIEFING_MAX_LENGTH } from '../services/settingsService';

const DEFAULT_TEMPLATE = `## Business Overview
- What we sell: <e.g. consumer electronics>
- Where we operate: <e.g. nationwide delivery within Nigeria>

## Order & Fulfillment
- Platform: <Shopify / WooCommerce / other>
- Only paid orders are processed.
- Shipment frequency: <e.g. twice daily, Mon–Sat>
- Delivery method: <e.g. nationwide via park dispatch>

## Customer Follow-up Rules
- Orders in "payment pending" → guide the customer to complete payment over chat.
- <add other recurring follow-up scenarios>

## When to Escalate to Staff
- <e.g. refund requests above ₦50,000>
- <e.g. damaged-item complaints>
- <e.g. wholesale / bulk pricing inquiries>

## Tone & Brand Voice
- <e.g. warm and friendly, local greetings OK>
- <anything specific your team says or avoids>

## Invoice / Order Confirmation Details
- Always confirm: name, phone, address, email.
- <other required fields>
`;

export default function BusinessBriefingSettings() {
  const { data: store, isLoading } = useStoreInfo();
  const updateStore = useUpdateStore();

  // Prefill the editor with the default template when the store has no
  // briefing saved yet. We do NOT auto-save the template — the field stays
  // NULL on the server until the admin explicitly hits Save.
  const initialDraft = useMemo(() => {
    if (!store) return '';
    return store.businessBriefing ?? DEFAULT_TEMPLATE;
  }, [store]);

  const [draft, setDraft] = useState<string>('');
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (!hydrated && store) {
      setDraft(initialDraft);
      setHydrated(true);
    }
  }, [hydrated, store, initialDraft]);

  const charCount = draft.length;
  const overLimit = charCount > BUSINESS_BRIEFING_MAX_LENGTH;
  const savedValue = store?.businessBriefing ?? '';
  const dirty = hydrated && draft.trim() !== savedValue.trim();

  const handleSave = async () => {
    if (overLimit) {
      toast.error(`Briefing must be ${BUSINESS_BRIEFING_MAX_LENGTH.toLocaleString()} characters or fewer.`);
      return;
    }
    try {
      await updateStore.mutateAsync({ business_briefing: draft });
      toast.success('Business briefing saved');
    } catch {
      // toast handled by hook
    }
  };

  const handleResetToTemplate = () => {
    if (!confirm('Replace the current draft with the default template? Unsaved changes will be lost.')) return;
    setDraft(DEFAULT_TEMPLATE);
  };

  const handleClear = async () => {
    if (!confirm('Clear the briefing? The agent will fall back to its built-in defaults until you save a new one.')) return;
    try {
      await updateStore.mutateAsync({ business_briefing: null });
      setDraft(DEFAULT_TEMPLATE);
      toast.success('Business briefing cleared');
    } catch {
      // toast handled by hook
    }
  };

  if (isLoading || !hydrated) {
    return <div className="py-8 text-center text-ink-3">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="rounded-panel border border-line bg-surface p-6 shadow-s">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-display text-lg font-semibold text-ink">Business Briefing</h3>
            <p className="mt-1 text-sm text-ink-2">
              Tell the AI agent how your business operates — fulfillment rules, follow-up
              triggers, escalation policy, brand voice. This is injected into every
              conversation so the agent speaks for your store, not a generic one.
            </p>
            <p className="mt-2 text-xs text-ink-3">
              Markdown supported. Changes take effect on the next message — no restart needed.
            </p>
          </div>
        </div>

        <div className="mt-4">
          {!savedValue && (
            <div className="mb-3 rounded-card border border-brand/20 bg-brand/5 px-4 py-3 text-xs text-brand">
              We've pre-filled the editor with a starter template. Replace the
              <span className="font-mono"> &lt;placeholders&gt; </span>
              with details specific to your business, then hit Save. Nothing is saved until you do.
            </div>
          )}

          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={24}
            spellCheck
            className="w-full rounded-xl border border-line bg-surface px-4 py-3 font-mono text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
            placeholder="Describe how your business operates..."
          />

          <div className="mt-2 flex items-center justify-between">
            <span
              className={`text-xs ${
                overLimit ? 'text-danger' : 'text-ink-3'
              }`}
            >
              {charCount.toLocaleString()} / {BUSINESS_BRIEFING_MAX_LENGTH.toLocaleString()} characters
              {overLimit && ' — over limit, please trim before saving'}
            </span>
            <span className="text-xs text-ink-3">
              {dirty ? 'Unsaved changes' : 'Saved'}
            </span>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={handleSave}
            disabled={updateStore.isPending || overLimit || !dirty}
            className="rounded-pill bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {updateStore.isPending ? 'Saving...' : 'Save'}
          </button>
          <button
            onClick={handleResetToTemplate}
            disabled={updateStore.isPending}
            className="rounded-pill border border-line px-4 py-2 text-sm font-medium text-ink hover:bg-black/[0.04] disabled:opacity-50"
          >
            Reset to template
          </button>
          {savedValue && (
            <button
              onClick={handleClear}
              disabled={updateStore.isPending}
              className="rounded-pill border border-danger/30 px-4 py-2 text-sm font-medium text-danger hover:bg-danger/10 disabled:opacity-50"
            >
              Clear briefing
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
